import { useEffect, useState, useCallback } from "react";
import { supabase } from "../supabaseClient";

const KEY_ATENDIDAS = "incidenciasAtendidas";
const KEY_ATENDIDAS_REPARTO = "incidenciasRepartoAtendidas";

export const leerIncidenciasAtendidas = () => {
  try {
    return JSON.parse(localStorage.getItem(KEY_ATENDIDAS)) || [];
  } catch {
    return [];
  }
};

export const marcarIncidenciaAtendida = (pedidoId) => {
  // El localStorage hace que el aviso desaparezca al instante, sin esperar a
  // la red. Pero es de este navegador: si el comercial lo lee en el móvil y
  // luego abre la app en la tableta, le volvería a saltar. Por eso además
  // queda registrado en el pedido.
  const actuales = leerIncidenciasAtendidas();
  if (!actuales.includes(String(pedidoId))) {
    localStorage.setItem(
      KEY_ATENDIDAS,
      JSON.stringify([...actuales, String(pedidoId)])
    );
  }
  window.dispatchEvent(new Event("incidencias-atendidas"));

  supabase
    .from("pedidos")
    .update({ incidencias_vistas_en: new Date().toISOString() })
    .eq("id", pedidoId)
    .is("incidencias_vistas_en", null)
    .then(() => {}, () => {
      // Sin conexión el aviso ya no sale en este aparato; se registrará
      // la próxima vez que lo abra con red
    });
};

export const leerIncidenciasRepartoAtendidas = () => {
  try {
    return JSON.parse(localStorage.getItem(KEY_ATENDIDAS_REPARTO)) || [];
  } catch {
    return [];
  }
};

// Mismo patrón que marcarIncidenciaAtendida, pero para las que registra el
// repartidor durante el reparto (negocio cerrado, cambio de día...).
export const marcarIncidenciaRepartoAtendida = (pedidoId) => {
  const actuales = leerIncidenciasRepartoAtendidas();
  if (!actuales.includes(String(pedidoId))) {
    localStorage.setItem(
      KEY_ATENDIDAS_REPARTO,
      JSON.stringify([...actuales, String(pedidoId)])
    );
  }
  window.dispatchEvent(new Event("incidencias-atendidas"));

  supabase
    .from("pedidos")
    .update({ incidencias_reparto_vistas_en: new Date().toISOString() })
    .eq("id", pedidoId)
    .is("incidencias_reparto_vistas_en", null)
    .then(() => {}, () => {});
};

// 01/11/2026: el repartidor marca "Entregado" y al comercial le sale el aviso
// hasta que lo lee. Se registra en el pedido para que no salga en otro aparato.
export const marcarEntregaVista = (pedidoId) => {
  window.dispatchEvent(new Event("incidencias-atendidas"));
  supabase
    .from("pedidos")
    .update({ entrega_vista_en: new Date().toISOString() })
    .eq("id", pedidoId)
    .is("entrega_vista_en", null)
    .then(() => {}, () => {});
};

export default function AvisoIncidencias({ usuario, setPagina, paginaActual }) {
  const [pendientes, setPendientes] = useState([]);
  const [oculto, setOculto] = useState(false);

  const comprobar = useCallback(async () => {
    if (!usuario?.usuario) return;

    // Solo los que el comercial no ha abierto todavía. El aviso insiste cada
    // vez que vuelve al menú hasta que lo lea; una vez leído no vuelve a
    // salir solo, y menos aún desde otro aparato.
    const hace3dias = new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString();
    const [{ data: dataPrep }, { data: dataReparto }, { data: dataEntregados }] = await Promise.all([
      supabase
        .from("pedidos")
        .select("id, numero, cliente, fecha")
        .eq("empresa_id", usuario.empresa_id)
        .eq("agente", usuario.usuario)
        .eq("estado_preparacion", "preparado_con_incidencias")
        .is("incidencias_vistas_en", null)
        .order("created_at", { ascending: false })
        .limit(20),
      supabase
        .from("pedidos")
        .select("id, numero, cliente, fecha")
        .eq("empresa_id", usuario.empresa_id)
        .eq("agente", usuario.usuario)
        .not("incidencias_reparto", "is", null)
        .is("incidencias_reparto_vistas_en", null)
        .order("created_at", { ascending: false })
        .limit(20),
      supabase
        .from("pedidos")
        .select("id, numero, cliente, fecha, entregado_por, entregado_centro_at")
        .eq("empresa_id", usuario.empresa_id)
        .eq("agente", usuario.usuario)
        .eq("estado_centro", "entregado")
        .is("entrega_vista_en", null)
        .gte("entregado_centro_at", hace3dias)
        .order("entregado_centro_at", { ascending: false })
        .limit(20),
    ]);

    const atendidas = leerIncidenciasAtendidas();
    const atendidasReparto = leerIncidenciasRepartoAtendidas();

    const prep = (dataPrep || [])
      .filter((p) => !atendidas.includes(String(p.id)))
      .map((p) => ({ ...p, tipo: "preparacion" }));
    const reparto = (dataReparto || [])
      .filter((p) => !atendidasReparto.includes(String(p.id)))
      .map((p) => ({ ...p, tipo: "reparto" }));

    const entregados = (dataEntregados || []).map((p) => ({ ...p, tipo: "entregado" }));

    setPendientes([...prep, ...reparto, ...entregados]);
  }, [usuario?.usuario, usuario?.empresa_id]);

  useEffect(() => {
    comprobar();
    const intervalo = setInterval(comprobar, 60000);
    window.addEventListener("incidencias-atendidas", comprobar);
    return () => {
      clearInterval(intervalo);
      window.removeEventListener("incidencias-atendidas", comprobar);
    };
  }, [comprobar]);

  // Al volver al menú (p. ej. tras enviar un pedido) se vuelve a comprobar
  // y el aviso reaparece si seguía sin atenderse
  useEffect(() => {
    setOculto(false);
    comprobar();
  }, [paginaActual, comprobar]);

  if (oculto || pendientes.length === 0) return null;

  // Si solo hay entregas, el aviso va en verde; si hay alguna incidencia, en rojo
  const soloEntregas = pendientes.every((p) => p.tipo === "entregado");
  const color = soloEntregas ? "#0D9488" : "#dc2626";

  return (
    <div style={{ ...caja, borderColor: color }}>
      <div style={{ ...cabecera, color }}>
        <span style={{ fontSize: 20 }}>{soloEntregas ? "📦" : "⚠️"}</span>
        <strong style={{ flex: 1 }}>
          {soloEntregas
            ? (pendientes.length === 1 ? "Pedido entregado" : `${pendientes.length} pedidos entregados`)
            : (pendientes.length === 1 ? "Aviso pendiente" : `${pendientes.length} avisos pendientes`)}
        </strong>
        <button onClick={() => setOculto(true)} style={btnCerrar}>✕</button>
      </div>

      <p style={texto}>Revisa en <strong>Mis pedidos</strong>:</p>

      {pendientes.slice(0, 3).map((p) => (
        <div key={`${p.tipo}-${p.id}`} style={{ ...lineaPedido, ...(p.tipo === "entregado" ? { background: "#F0FDFA" } : {}) }}>
          <strong>Pedido #{p.numero}</strong>
          <span style={{ color: "#64748b" }}>
            {" "}— {p.cliente?.nombreComercial || p.cliente?.nombreFiscal || "-"}
          </span>
          <div style={{ fontSize: 11.5, color: p.tipo === "entregado" ? "#0D9488" : p.tipo === "reparto" ? "#B45309" : "#dc2626", fontWeight: 700, marginTop: 2 }}>
            {p.tipo === "entregado"
              ? `📦 Entregado${p.entregado_por ? ` por ${p.entregado_por}` : ""}${p.entregado_centro_at ? ` · ${new Date(p.entregado_centro_at).toLocaleString("es-ES", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}` : ""}`
              : p.tipo === "reparto" ? "🚚 Incidencia de reparto" : "Incidencia de preparación"}
          </div>
        </div>
      ))}

      {pendientes.length > 3 && (
        <p style={{ ...texto, marginTop: 4 }}>
          …y {pendientes.length - 3} más
        </p>
      )}

      {/* Pulsar aquí cuenta como leído. Antes solo se marcaba al abrir el
          informe de cada pedido uno por uno, así que el comercial venía a
          verlos, se hacía cargo, y el aviso le seguía saltando cada vez que
          volvía al menú. */}
      <button
        onClick={() => {
          pendientes.forEach((p) =>
            p.tipo === "entregado" ? marcarEntregaVista(p.id)
              : p.tipo === "reparto" ? marcarIncidenciaRepartoAtendida(p.id) : marcarIncidenciaAtendida(p.id)
          );
          setOculto(true);
          if (setPagina) setPagina("misPedidos");
        }}
        style={{ ...btnVer, background: color }}
      >
        {soloEntregas ? "Ver mis pedidos" : "Ver pedidos con incidencias"}
      </button>
    </div>
  );
}

const caja = {
  position: "fixed",
  bottom: 20,
  right: 20,
  zIndex: 3000,
  width: 340,
  maxWidth: "calc(100vw - 40px)",
  background: "white",
  borderRadius: 18,
  padding: 16,
  boxShadow: "0 16px 48px rgba(15,23,42,0.28)",
  border: "2px solid #dc2626",
};

const cabecera = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  color: "#dc2626",
  marginBottom: 6,
};

const btnCerrar = {
  border: "none",
  background: "#f1f5f9",
  borderRadius: 8,
  padding: "4px 9px",
  cursor: "pointer",
  fontSize: 14,
  color: "#334E68",
};

const texto = {
  margin: "4px 0 8px",
  fontSize: 13,
  color: "#334E68",
};

const lineaPedido = {
  fontSize: 13,
  padding: "6px 10px",
  background: "#fef2f2",
  borderRadius: 10,
  marginBottom: 6,
  color: "#0f172a",
};

const btnVer = {
  width: "100%",
  marginTop: 6,
  border: "none",
  background: "#dc2626",
  color: "white",
  borderRadius: 12,
  padding: "12px",
  fontWeight: "800",
  fontSize: 14,
  cursor: "pointer",
};
